import { useEffect, useState } from 'react';
import { api, ApiError, type IdeaGroup, type RecipeSearchResponse } from '../api';
import { Button, ErrorNote, Sheet, Skeleton, inputClass } from '../ui';

/**
 * Turning an idea into the actual recipe (plan revision R14).
 *
 * An idea is a name on an old list. Planning one used to put that name on a
 * night and nothing in the shop, so every week meant finding the recipe by
 * hand, importing it, then deleting the idea that had become redundant — the
 * app creating work rather than removing it.
 *
 * The order of the three routes below is the order of how much they can be
 * trusted, and the UI keeps that visible rather than blending them:
 *
 *   1. Found on a blog this household already cooks from. Real URLs, taken from
 *      the search API's own result blocks rather than from model prose.
 *   2. A URL pasted by hand, for anywhere else.
 *   3. Written by Claude — offered only after a search has actually come back
 *      empty, and badged as written wherever it appears afterwards.
 */

type Adopted = { recipe_id: string; title: string; source_url?: string; basis?: string };

export function FindRecipeSheet({
  idea,
  onClose,
  onAdopted,
}: {
  idea: IdeaGroup;
  onClose: () => void;
  onAdopted: () => void;
}) {
  const [result, setResult] = useState<RecipeSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [done, setDone] = useState<Adopted | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .post<RecipeSearchResponse>('/ideas/find-recipe', { dish: idea.idea_text })
      .then((r) => { if (!cancelled) setResult(r); })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Could not search for that.');
      });
    return () => { cancelled = true; };
  }, [idea.idea_text]);

  async function adopt(url: string) {
    setBusy(url);
    setError(null);
    try {
      setDone(await api.post<Adopted>('/ideas/adopt-recipe', { ids: idea.ids, url }));
    } catch (e) {
      // A blocked or unparseable page is expected often enough to be a normal
      // outcome rather than a failure (plan section 7.1) — say so and leave the
      // other candidates on screen.
      setError(e instanceof ApiError ? e.message : 'Could not import that page.');
    } finally {
      setBusy(null);
    }
  }

  async function write() {
    setBusy('write');
    setError(null);
    try {
      setDone(
        await api.post<Adopted>('/ideas/write-recipe', {
          ids: idea.ids,
          dish: idea.idea_text,
          note: result?.note ?? null,
        }),
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not write that recipe.');
    } finally {
      setBusy(null);
    }
  }

  if (done) {
    return (
      <Sheet title={done.title} onClose={() => { onAdopted(); onClose(); }}>
        <p className="rounded-xl bg-accent-soft px-3.5 py-2.5 text-sm text-accent-deep">
          Saved as a recipe, and &ldquo;{idea.idea_text}&rdquo; has left your ideas list.
        </p>
        {done.source_url && (
          <p className="mt-3 break-all text-xs text-text-muted">From {done.source_url}</p>
        )}
        {done.basis && (
          <p className="mt-3 text-sm text-text-muted">
            Written by Claude rather than found on your blogs. Based on: {done.basis}
          </p>
        )}
        <Button className="mt-5 w-full" onClick={() => { onAdopted(); onClose(); }}>
          Done
        </Button>
      </Sheet>
    );
  }

  const hits = result?.hits ?? [];
  const searchFailed = !!result?.searchError;

  return (
    <Sheet title={`Find “${idea.idea_text}”`} onClose={onClose}>
      {!result && !error && (
        <>
          <p className="mb-3 text-sm text-text-muted">Searching the blogs you cook from…</p>
          <Skeleton rows={3} />
        </>
      )}

      {error && <div className="mb-3"><ErrorNote>{error}</ErrorNote></div>}

      {result?.note && <p className="mb-3 text-sm text-text-primary">{result.note}</p>}

      {searchFailed && (
        <p className="mb-3 rounded-xl bg-accent-soft px-3.5 py-2.5 text-sm text-accent-deep">
          The search itself didn&rsquo;t run ({result?.searchError}). Nothing was found — that is a
          failure rather than an empty result, so it is worth trying again.
        </p>
      )}

      {hits.length > 0 && (
        <ul className="overflow-hidden rounded-card bg-bg-raised">
          {hits.map((h, idx) => (
            <li key={h.url} className={idx > 0 ? 'border-t border-border-subtle' : ''}>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void adopt(h.url)}
                className="tap w-full px-3.5 py-3 text-left transition-colors active:bg-bg-inset
                           disabled:opacity-50"
              >
                <span className="block text-[15px] leading-snug text-text-primary">{h.title}</span>
                {/* The domain in the display face: which blog this came from is
                    the trust signal, so it should read as a label not a
                    footnote. */}
                <span className="mt-1 block font-display text-[10px] uppercase tracking-wider text-accent-vibrant">
                  {busy === h.url ? 'Importing…' : h.host}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {result && hits.length === 0 && !searchFailed && (
        <p className="rounded-xl border border-border-subtle bg-bg-base px-3.5 py-2.5 text-sm text-text-muted">
          Nothing on your blogs matched this one. Searched {result.searched.length} sites:{' '}
          {result.searched.join(', ')}.
        </p>
      )}

      {/* Named rather than quietly omitted: a site you asked for that was not
          searched is a gap in the result, not an absence of recipes. */}
      {result && result.skipped.length > 0 && (
        <p className="mt-3 px-1 text-xs text-text-muted">
          {result.skipped.map((s) => s.name).join(' and ')}{' '}
          {result.skipped.length === 1 ? 'blocks' : 'block'} automated search, so{' '}
          {result.skipped.length === 1 ? 'it was' : 'they were'} not included. Paste a link from{' '}
          {result.skipped.length === 1 ? 'it' : 'them'} below instead.
        </p>
      )}

      <div className="mt-5 border-t border-border-subtle pt-4">
        <p className="mb-2 font-display text-[11px] uppercase tracking-[0.1em] text-text-secondary">Have the link already?</p>
        <input
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          placeholder="Paste a recipe URL"
          className={inputClass}
        />
        <Button
          variant="secondary"
          className="mt-2 w-full"
          disabled={busy !== null || pasted.trim().length < 4}
          onClick={() => void adopt(pasted.trim())}
        >
          {busy === pasted.trim() ? 'Importing…' : 'Import this one'}
        </Button>
      </div>

      {/* Offered only once a search has actually happened and come back empty,
          so it reads as a last resort rather than an equal option. */}
      {result && hits.length === 0 && (
        <div className="mt-5 border-t border-border-subtle pt-4">
          <Button
            variant="secondary"
            className="w-full"
            disabled={busy !== null}
            onClick={() => void write()}
          >
            {busy === 'write' ? 'Writing…' : 'Have Claude write one'}
          </Button>
          <p className="px-1 pt-2 text-xs text-text-muted">
            Saved and labelled as written by Claude, not found on your blogs — so you can always
            tell which recipes came from somewhere you trust.
          </p>
        </div>
      )}
    </Sheet>
  );
}
